import { useEffect, useState } from 'react';
import { logEvent, analytics } from '../firebase.js';
import { CollectionService, COLLECTION_NAME_MAX } from '../services/collection-service.js';
import { useLocale } from '../hooks/useLocale.jsx';

export function AddToCollectionModal({ designId, user, onClose }) {
  const { t } = useLocale();
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [memberSet, setMemberSet] = useState(new Set());
  const [pending, setPending] = useState(new Set()); // ids in flight
  const [creatingName, setCreatingName] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user || user.isAnonymous) return;
    let mounted = true;
    (async () => {
      try {
        let list = await CollectionService.listMine(user.uid);
        // First-time user: auto-create the default 「Saved」 collection so
        // they have somewhere to save without forcing a board name first.
        if (!mounted) return;
        if (list.length === 0) {
          await CollectionService.ensureDefault(user.uid, t('savedCollectionDefault'));
          list = await CollectionService.listMine(user.uid);
        }
        if (!mounted) return;
        setCollections(list);
        const set = await CollectionService.checkMembership(list, designId);
        if (!mounted) return;
        setMemberSet(set);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [user, designId, t]);

  const setBusy = (id, on) => {
    setPending(prev => {
      const next = new Set(prev);
      on ? next.add(id) : next.delete(id);
      return next;
    });
  };

  const toggleMembership = async (col) => {
    if (pending.has(col.id)) return;
    setBusy(col.id, true);
    const wasMember = memberSet.has(col.id);
    // Optimistic
    setMemberSet(prev => {
      const next = new Set(prev);
      wasMember ? next.delete(col.id) : next.add(col.id);
      return next;
    });
    try {
      if (wasMember) {
        await CollectionService.removeItem(col.id, designId);
        logEvent(analytics, 'collection_item_removed', { collectionId: col.id });
      } else {
        await CollectionService.addItem(col.id, designId);
        logEvent(analytics, 'collection_item_added', { collectionId: col.id });
      }
      // Auto-close on any successful toggle — user expects single action then done.
      onClose?.();
    } catch (err) {
      console.error('toggleMembership:', err);
      // Revert on failure
      setMemberSet(prev => {
        const next = new Set(prev);
        wasMember ? next.add(col.id) : next.delete(col.id);
        return next;
      });
      setError(t('collectionErrSave'));
    } finally {
      setBusy(col.id, false);
    }
  };

  const handleCreate = async () => {
    if (!creatingName.trim() || creating) return;
    setCreating(true);
    setError(null);
    try {
      const id = await CollectionService.create({ name: creatingName, isPublic: false });
      await CollectionService.addItem(id, designId);
      logEvent(analytics, 'collection_created', { withInitialItem: true });
      // Created + design auto-added — close immediately (no further action needed).
      onClose?.();
    } catch (err) {
      console.error('create collection:', err);
      setError(t('collectionErrCreate'));
      setCreating(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card add-collection-modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close-btn" onClick={onClose} aria-label="Close">
          <i className="material-symbols-rounded">close</i>
        </button>
        <h3>{t('saveToCollection')}</h3>

        <div className="add-collection-create">
          <input
            type="text"
            className="edit-field-input add-collection-name-input"
            placeholder={t('collectionNamePlaceholder')}
            value={creatingName}
            onChange={e => setCreatingName(e.target.value.slice(0, COLLECTION_NAME_MAX))}
            maxLength={COLLECTION_NAME_MAX}
            disabled={creating}
            onKeyDown={e => { if (e.key === 'Enter') handleCreate(); }}
          />
          <button
            className="btn btn-primary add-collection-create-btn"
            onClick={handleCreate}
            disabled={creating || !creatingName.trim()}
          >
            {t('collectionCreate')}
          </button>
        </div>

        {error && <p className="account-form-error">{error}</p>}

        {loading ? (
          <div className="loading" style={{ minHeight: '120px' }}><div className="spinner"></div></div>
        ) : collections.length === 0 ? (
          <p className="comments-empty">{t('collectionEmpty')}</p>
        ) : (
          <>
            <p className="add-collection-hint">{t('collectionPickerHint')}</p>
            <ul className="add-collection-list">
              {collections.map(c => {
                const isMember = memberSet.has(c.id);
                return (
                  <li key={c.id}>
                    <button
                      type="button"
                      className={`add-collection-row ${isMember ? 'is-member' : ''}`}
                      onClick={() => toggleMembership(c)}
                      disabled={pending.has(c.id)}
                    >
                      <i className="material-symbols-rounded">{isMember ? 'check_circle' : 'radio_button_unchecked'}</i>
                      <span className="add-collection-name">{c.name}</span>
                      <span className="add-collection-count">{t('collectionItemCount', { n: c.itemCount || 0 })}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </>
        )}

      </div>
    </div>
  );
}
